import React, { useState, useEffect } from 'react';
import { AuditLog, AdminLogsQuery, AdminLogsExportQuery, AdminLogsResponse } from '@df-gate-share/shared';
import { apiClient } from '../../lib/api';
import MessageDialog from '../../components/MessageDialog';
import { useMessageDialog } from '../../hooks/useMessageDialog';

type ResultFilter = 'all' | 'success' | 'failure';

const AuditLogs: React.FC = () => {
  const { dialogState, closeDialog, showAlert } = useMessageDialog();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [pagination, setPagination] = useState<{
    hasMore: boolean;
    nextToken: string | null;
  }>({
    hasMore: false,
    nextToken: null
  });
  // tokenHistory stores the nextToken used to fetch each previous page (null = first page)
  const [tokenHistory, setTokenHistory] = useState<(string | null)[]>([]);
  // currentToken is the nextToken used to fetch the currently displayed page
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    userEmail: '',
    action: '',
    result: 'all' as ResultFilter,
    ipAddress: ''
  });
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [customQuery, setCustomQuery] = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (autoRefresh) {
      intervalId = setInterval(() => {
        fetchLogs();
      }, 30000); // 30 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, filters]);

  const fetchLogs = async (isCustomQuery = false, nextToken: string | null = null, resetHistory = false, filterOverrides?: Partial<typeof filters>) => {
    setLoading(true);
    setError('');

    const startTime = Date.now();
    const activeFilters = filterOverrides ? { ...filters, ...filterOverrides } : filters;

    try {
      const params = new URLSearchParams();

      if (isCustomQuery && customQuery) {
        params.append('customQuery', customQuery);
      } else {
        // Convert date strings (YYYY-MM-DD) to ISO 8601 timestamps
        if (activeFilters.startDate) {
          const startTime = new Date(activeFilters.startDate + 'T00:00:00.000Z').toISOString();
          params.append('startTime', startTime);
        }
        if (activeFilters.endDate) {
          const endTime = new Date(activeFilters.endDate + 'T23:59:59.999Z').toISOString();
          params.append('endTime', endTime);
        }
        if (activeFilters.userEmail) params.append('userEmail', activeFilters.userEmail);
        if (activeFilters.action) params.append('action', activeFilters.action);
        if (activeFilters.result !== 'all') params.append('result', activeFilters.result);
        if (activeFilters.ipAddress) params.append('ipAddress', activeFilters.ipAddress);
      }

      if (nextToken) {
        params.append('nextToken', nextToken);
      }
      params.append('limit', '50');

      const response = await apiClient.get(`/api/v1/admin/logs?${params.toString()}`);
      const data: AdminLogsResponse = response.data;
      setLogs(data.logs || []);
      setPagination({
        hasMore: data.pagination?.hasMore || false,
        nextToken: data.pagination?.nextToken || null
      });

      if (resetHistory) {
        setTokenHistory([]);
        setCurrentToken(null);
      }

      const duration = Date.now() - startTime;
      if (duration > 30000) {
        showAlert('warning', '検索時間の警告', '検索に30秒以上かかりました。フィルター条件を絞ることをお勧めします。');
      }
    } catch (err) {
      setError('監査ログの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasMore && pagination.nextToken) {
      // Save the token used to fetch the current page before navigating forward
      setTokenHistory([...tokenHistory, currentToken]);
      setCurrentToken(pagination.nextToken);
      fetchLogs(false, pagination.nextToken);
    }
  };

  const handlePrevPage = () => {
    if (tokenHistory.length > 0) {
      const newHistory = [...tokenHistory];
      const prevToken = newHistory.pop() ?? null;
      setTokenHistory(newHistory);
      setCurrentToken(prevToken);
      fetchLogs(false, prevToken);
    }
  };

  const handleSearch = () => {
    setTokenHistory([]);
    setCurrentToken(null);
    fetchLogs(false, null, true);
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const params = new URLSearchParams();
      // Convert date strings (YYYY-MM-DD) to ISO 8601 timestamps
      if (filters.startDate) {
        const startTime = new Date(filters.startDate + 'T00:00:00.000Z').toISOString();
        params.append('startTime', startTime);
      }
      if (filters.endDate) {
        const endTime = new Date(filters.endDate + 'T23:59:59.999Z').toISOString();
        params.append('endTime', endTime);
      }
      if (filters.userEmail) params.append('userEmail', filters.userEmail);
      if (filters.action) params.append('action', filters.action);
      if (filters.result !== 'all') params.append('result', filters.result);
      if (filters.ipAddress) params.append('ipAddress', filters.ipAddress);
      params.append('format', format);

      const response = await apiClient.get(`/api/v1/admin/logs/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showAlert('error', 'エラー', `${format.toUpperCase()}エクスポートに失敗しました`);
    }
  };

  const formatDate = (dateString: string): string => {
    // CloudWatch Logs Insights returns @timestamp as "YYYY-MM-DD HH:mm:ss.SSS" (UTC, no timezone indicator).
    // Append 'Z' to ensure it is parsed as UTC before converting to local time.
    const normalized = dateString.includes('T') ? dateString : dateString.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Tokyo',
    });
  };

  const ACTION_LABELS: Record<string, string> = {
    // 認証
    'auth.login': 'ログイン',
    'auth.login.oidc': 'ログイン（SSO）',
    'auth.logout': 'ログアウト',
    'auth.refresh': 'トークン更新',
    'auth.password-reset': 'パスワードリセット要求',
    'auth.password-reset-confirm': 'パスワードリセット実行',
    'auth.password-change': 'パスワード変更',
    // ファイル操作
    'file.upload': 'ファイルアップロード',
    'file.upload-url': 'アップロードURL発行',
    'file.upload-complete': 'アップロード完了',
    'file.delete': 'ファイル削除',
    'file.download': 'ファイルダウンロード',
    'file.scan-result': 'ウイルススキャン結果',
    'file.get-detail': 'ファイル詳細取得',
    // 共有操作
    'share.create': '共有リンク作成',
    'share.revoke': '共有リンク無効化',
    'share.update-expiry': '共有有効期限変更',
    'share.download': '共有ファイルダウンロード',
    'share.send-otp': 'OTP送信',
    'share.verify-otp': 'OTP認証',
    'share.get': '共有詳細取得',
    'share.list': '共有一覧取得',
    'share.get-public': '共有公開情報取得',
    // 受信
    'received.list': '受信ファイル一覧取得',
    // 連絡先操作
    'contact.create': '連絡先作成',
    'contact.update': '連絡先更新',
    'contact.delete': '連絡先削除',
    'contact.authorize-google': 'Google連携開始',
    'contact.callback-google': 'Google連携完了',
    'contact.disconnect-google': 'Google連携解除',
    'contact.list': '連絡先一覧取得',
    'contact.get': '連絡先取得',
    'contact.check-trust': '信頼リストチェック',
    // 管理者操作
    'admin.create-user': 'ユーザー作成（管理者）',
    'admin.update-user': 'ユーザー更新（管理者）',
    'admin.disable-user': 'ユーザー無効化（管理者）',
    'admin.reset-password': 'パスワードリセット（管理者）',
    'admin.delete-file': 'ファイル緊急削除（管理者）',
    'admin.revoke-share': '共有無効化（管理者）',
    'admin.get-logs': '監査ログ取得（管理者）',
    'admin.export-logs': '監査ログエクスポート（管理者）',
    'admin.get-stats': '統計情報取得（管理者）',
    'admin.list-shares': '共有一覧取得（管理者）',
    'admin.list-users': 'ユーザー一覧取得（管理者）',
    // お知らせ操作（管理者）
    'announcement.create': 'お知らせ作成',
    'announcement.list': 'お知らせ一覧取得',
    'announcement.get': 'お知らせ取得',
    'announcement.update': 'お知らせ更新',
    'announcement.delete': 'お知らせ削除',
    // お知らせ操作（一般ユーザー）
    'announcement.user.list': 'お知らせ一覧取得（利用者）',
    'announcement.user.get': 'お知らせ取得（利用者）',
    'announcement.user.read': 'お知らせ既読（利用者）',
    'announcement.user.unreadCount': 'お知らせ未読数取得（利用者）',
    // 通知設定（利用者）
    'user.notification-preferences.update': 'メール通知設定の更新（利用者）',
  };

  const RESOURCE_TYPE_LABELS: Record<string, string> = {
    'user': 'ユーザー',
    'file': 'ファイル',
    'share': '共有リンク',
    'token': 'トークン',
    'contact': '連絡先',
    'announcement': 'お知らせ',
  };

  const getActionLabel = (action: string): string => {
    return ACTION_LABELS[action] || action;
  };

  const getResourceTypeLabel = (resourceType: string): string => {
    return RESOURCE_TYPE_LABELS[resourceType] || resourceType;
  };

  const getActionColor = (action: string): string => {
    if (action.includes('delete') || action.includes('revoke') || action.includes('disable')) {
      return '#dc3545';
    }
    if (action.includes('create') || action.includes('upload')) {
      return '#28a745';
    }
    if (action.includes('update') || action.includes('reset')) {
      return '#ffc107';
    }
    return '#007bff';
  };

  const presetFilters = [
    {
      name: '失敗したログイン',
      filters: { action: 'auth.login', result: 'failure' as ResultFilter }
    },
    {
      name: '管理者操作',
      filters: { action: 'admin.', result: 'all' as ResultFilter }
    },
    {
      name: 'ファイル削除',
      filters: { action: 'file.delete', result: 'all' as ResultFilter }
    },
    {
      name: '共有無効化',
      filters: { action: 'share.revoke', result: 'all' as ResultFilter }
    }
  ];

  return (
    <div className="card card__body">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="page-title">監査ログ</h1>

          <div className="flex gap-3 items-center">
            <label className="flex items-center gap-2 text-sm text-muted" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              自動更新（30秒）
            </label>
            <button
              onClick={() => handleExport('csv')}
              className="btn btn--sm btn--success"
            >
              <span className="material-symbols-outlined icon-sm">bar_chart</span> CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="btn btn--sm btn--primary"
            >
              <span className="material-symbols-outlined icon-sm">inventory_2</span> JSON
            </button>
          </div>
        </div>

        {/* Preset Filters */}
        <div className="flex gap-3 flex-wrap items-center">
          <span className="text-sm text-muted">クイックフィルター:</span>
          {presetFilters.map((preset, index) => (
            <button
              key={index}
              onClick={() => {
                const newFilters = { ...filters, ...preset.filters };
                setFilters(newFilters);
                fetchLogs(false, null, true, preset.filters);
              }}
              className="filter-btn"
              style={{ borderRadius: 'var(--radius-full)' }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="card card__body mb-6" style={{ background: 'var(--color-bg)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
          <div>
            <label className="form-label">開始日時</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">終了日時</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">ユーザーメール</label>
            <input
              type="text"
              value={filters.userEmail}
              onChange={(e) => setFilters({ ...filters, userEmail: e.target.value })}
              placeholder="user@example.com"
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">アクション</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="form-select"
            >
              <option value="">すべて</option>
              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">結果</label>
            <select
              value={filters.result}
              onChange={(e) => setFilters({ ...filters, result: e.target.value as ResultFilter })}
              className="form-select"
            >
              <option value="all">すべて</option>
              <option value="success">成功</option>
              <option value="failure">失敗</option>
            </select>
          </div>

          <div>
            <label className="form-label">IPアドレス</label>
            <input
              type="text"
              value={filters.ipAddress}
              onChange={(e) => setFilters({ ...filters, ipAddress: e.target.value })}
              placeholder="203.0.113.42"
              className="form-input"
            />
          </div>
        </div>

        <div className="flex gap-3 items-center mt-4">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="btn btn--primary"
          >
            {loading ? '検索中...' : <><span className="material-symbols-outlined icon-sm">search</span> 検索</>}
          </button>
          <button
            onClick={() => {
              setFilters({
                startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                userEmail: '',
                action: '',
                result: 'all',
                ipAddress: ''
              });
              setTokenHistory([]);
              setCurrentToken(null);
            }}
            className="btn btn--secondary"
          >
            リセット
          </button>
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className={`btn btn--sm ${showAdvancedSearch ? 'btn--warning' : 'btn--secondary'}`}
          >
            <span className="material-symbols-outlined icon-sm">settings</span> 高度な検索
          </button>
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="card card__body mt-4" style={{ border: '1px solid var(--color-border)' }}>
            <label className="form-label">
              カスタムクエリ（CloudWatch Logs Insights形式）
            </label>
            <textarea
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
              placeholder="fields @timestamp, userId, action, result | filter action like /file./ | sort @timestamp desc"
              rows={4}
              className="form-textarea font-mono"
            />
            <button
              onClick={() => fetchLogs(true)}
              disabled={!customQuery.trim() || loading}
              className="btn btn--primary btn--sm mt-4"
            >
              カスタムクエリを実行
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert--error">{error}</div>
      )}

      {/* Results Count */}
      {!loading && logs.length > 0 && (
        <div className="text-sm text-muted mb-4">
          {logs.length}件のログが見つかりました
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center text-muted" style={{ padding: 'var(--space-10)' }}>
          読み込み中...
        </div>
      ) : (
        <>
          {/* Logs Table */}
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>日時</th>
                  <th>ユーザー</th>
                  <th>アクション</th>
                  <th>結果</th>
                  <th>リソース</th>
                  <th>IPアドレス</th>
                  <th style={{ textAlign: 'center' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => (
                  <tr
                    key={`${log.requestId}-${index}`}
                    style={{
                      backgroundColor:
                        log.result === 'failure' ? 'var(--color-danger-bg)' :
                        log.action?.startsWith('admin.') ? 'var(--color-warning-bg)' :
                        undefined
                    }}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(log.timestamp)}
                    </td>
                    <td style={{ color: 'var(--color-text-primary)' }}>
                      {log.userEmail}
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: `${getActionColor(log.action)}22`,
                          color: getActionColor(log.action),
                          border: `1px solid ${getActionColor(log.action)}44`
                        }}
                        title={log.action}
                      >
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${log.result === 'success' ? 'badge--success' : 'badge--danger'}`}>
                        {log.result === 'success' ? <><span className="material-symbols-outlined icon-sm">check</span> 成功</> : <><span className="material-symbols-outlined icon-sm">close</span> 失敗</>}
                      </span>
                    </td>
                    <td>
                      {log.resourceType && (
                        <span className="text-xs" title={log.resourceType}>
                          {getResourceTypeLabel(log.resourceType)}
                        </span>
                      )}
                    </td>
                    <td className="font-mono">
                      {log.ipAddress}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          setSelectedLog(log);
                          setShowDetailModal(true);
                        }}
                        className="btn btn--sm btn--primary"
                        title="詳細を表示"
                      >
                        <span className="material-symbols-outlined icon-sm">description</span> 詳細
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {logs.length === 0 && (
              <div className="empty-state">
                <div className="empty-state__desc">監査ログが見つかりません</div>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {logs.length > 0 && (
            <div className="pagination">
              <div className="pagination__info">
                {logs.length}件の結果を表示中
              </div>
              <div className="pagination__buttons">
                <button
                  onClick={handlePrevPage}
                  disabled={tokenHistory.length === 0}
                  className="btn btn--primary btn--sm"
                >
                  ← 前へ
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                  className="btn btn--primary btn--sm"
                >
                  次へ →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="modal-overlay">
          <div className="modal modal--wide">
            <h2 className="modal__title">ログ詳細</h2>

            <div className="font-mono" style={{ fontSize: 'var(--text-sm)', backgroundColor: 'var(--color-bg)', padding: 'var(--space-5)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify({
                  timestamp: selectedLog.timestamp,
                  requestId: selectedLog.requestId,
                  userId: selectedLog.userId,
                  userEmail: selectedLog.userEmail,
                  action: selectedLog.action,
                  result: selectedLog.result,
                  resourceType: selectedLog.resourceType,
                  resourceId: selectedLog.resourceId,
                  ipAddress: selectedLog.ipAddress,
                  userAgent: selectedLog.userAgent,
                  details: selectedLog.details
                }, null, 2)}
              </pre>
            </div>

            <div className="modal__actions">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedLog(null);
                }}
                className="btn btn--secondary"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </div>
  );
};

export default AuditLogs;
